/**
 * Registro do service worker (at-114, ADR 0059).
 *
 * Adaptado do template `cra-template-pwa`, com duas diferenças deliberadas:
 *
 * 1. 🔴 A versão nova ENTRA SOZINHA (oficina/at-136, decisão do Victor de 27/10/2026 — reverte o
 *    aceite por toast da at-114). Com o aceite, um navegador que nunca clicou ficava dias no
 *    bundle antigo: foi assim que a RFM1C23 e a KVQ9873 foram cadastradas sem o valor FIPE, por
 *    um frontend anterior a 21/10. O que estava sendo preenchido sobrevive por RASCUNHO: antes de
 *    recarregar, `aplicarAtualizacao` avisa a tela (`EVENTO_ANTES_DE_RECARREGAR`) e espera ela
 *    gravar.
 * 2. Em desenvolvimento ele **não** é registrado, a menos que `REACT_APP_SW=1`: um service
 *    worker ativo em `localhost` serve bundle em cache e faz mudança de código "não aparecer",
 *    que é um dia inteiro de depuração perdido.
 */

/** Disparado na janela antes de a versão nova recarregar a página. Quem tem rascunho grava na hora. */
export const EVENTO_ANTES_DE_RECARREGAR = 'soac:antes-de-recarregar';

/** Quanto a página espera as telas gravarem os rascunhos antes de recarregar. */
const ESPERA_DOS_RASCUNHOS_MS = 2000;

/**
 * A aba da recepção fica visível o dia inteiro, então só o `visibilitychange` não basta: sem
 * conferência periódica, ela nunca perguntaria se há versão nova.
 */
export const INTERVALO_DE_CONFERENCIA_MS = 15 * 60 * 1000;

/**
 * Pede às telas que gravem o que está sendo preenchido e espera (com teto). Cada ouvinte recebe
 * `detail.aguardar(promessa)` para dizer o que precisa terminar antes do reload.
 */
export async function aguardarRascunhos(tetoMs = ESPERA_DOS_RASCUNHOS_MS): Promise<void> {
  const pendentes: Promise<unknown>[] = [];
  window.dispatchEvent(
    new CustomEvent(EVENTO_ANTES_DE_RECARREGAR, { detail: { aguardar: (p: Promise<unknown>) => pendentes.push(p) } }),
  );
  if (!pendentes.length) return;
  await Promise.race([
    Promise.allSettled(pendentes),
    new Promise((resolve) => setTimeout(resolve, tetoMs)),
  ]);
}

const ehLocalhost = Boolean(
  typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '[::1]' ||
      /^127(?:\.\d{1,3}){3}$/.test(window.location.hostname)),
);

export interface OpcoesDeRegistro {
  onSuccess?: (registro: ServiceWorkerRegistration) => void;
}

/**
 * O que fazer quando o worker termina de instalar. Exportado para o teste: é aqui que mora a regra
 * "atualização não espera clique".
 */
export function aoInstalar(registro: ServiceWorkerRegistration, opcoes: OpcoesDeRegistro = {}): void {
  if (navigator.serviceWorker.controller) {
    // Já havia uma versão controlando a página: esta é uma ATUALIZAÇÃO, e entra sozinha.
    void aplicarAtualizacao(registro);
  } else {
    opcoes.onSuccess?.(registro);
  }
}

export function register(opcoes: OpcoesDeRegistro = {}): void {
  if (process.env.NODE_ENV === 'test') return;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (ehLocalhost && process.env.REACT_APP_SW !== '1') return;

  const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
  // O SW só controla páginas da própria origem — com CDN noutro domínio, ele não funcionaria.
  if (publicUrl.origin !== window.location.origin) return;

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    navigator.serviceWorker
      .register(swUrl)
      .then((registro) => {
        registro.onupdatefound = () => {
          const instalando = registro.installing;
          if (!instalando) return;

          instalando.onstatechange = () => {
            if (instalando.state !== 'installed') return;
            aoInstalar(registro, opcoes);
          };
        };

        // Versão que terminou de instalar antes desta carga (aba aberta noutra hora) já está
        // esperando: aplica sem precisar de um `updatefound` novo.
        if (registro.waiting && navigator.serviceWorker.controller) void aplicarAtualizacao(registro);

        setInterval(() => {
          void registro.update().catch(() => undefined);
        }, INTERVALO_DE_CONFERENCIA_MS);
      })
      .catch(() => {
        // Silêncio deliberado: falha ao registrar o service worker não é chamada de API e não
        // muda nada para quem está usando o sistema agora — o app funciona normalmente online.
        // O que ela custa é o modo offline, e avisar disso a cada carregamento seria ruído para
        // os papéis que nunca ficam sem rede.
      });

    // Confere se há versão nova a cada volta para a aba.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void navigator.serviceWorker.ready.then((registro) => registro.update()).catch(() => undefined);
      }
    });
  });
}

let aplicando = false;

/**
 * Aplica a versão nova: grava os rascunhos, manda o SW assumir e recarrega quando ele assumir de
 * fato. Idempotente — `updatefound` e a conferência da carga podem chegar juntos.
 */
export async function aplicarAtualizacao(registro: ServiceWorkerRegistration): Promise<void> {
  if (aplicando) return;
  aplicando = true;
  await aguardarRascunhos();

  if (!registro.waiting) {
    window.location.reload();
    return;
  }

  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
    once: true,
  });
  registro.waiting.postMessage({ type: 'SKIP_WAITING' });
}

/** Só para teste: o módulo guarda estado entre chamadas. */
export function __reiniciarParaTeste(): void {
  aplicando = false;
}

export function unregister(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  void navigator.serviceWorker.ready
    .then((registro) => registro.unregister())
    .catch(() => undefined);
}
